import { useQuery } from '@apollo/client';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AnalyticsIcon, FeedbackIcon, Overview } from 'assets/img/menu';
import Loader from 'Components/Forms/Loader';
import ProductAnalyticsSmall from 'FeatureModules/ProductPage/Components/ProductAnalyticsSmall';
import ShareSummaryCard from 'FeatureModules/ProductPage/Components/Cards/ShareSummaryCard';
import ProductStatusCard from 'FeatureModules/ProductPage/Components/ProductStatusCard';
import ErrorState from 'Components/ErrorState';
import HasAccess from 'Components/HasAccess';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import { Tabs } from 'DesignSystem';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { GET_PRODUCT_AND_ANALYTICS_EXIST } from 'graphql/secure/queries';
import React from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { TRouteParamsWithId } from 'types/router.types';

export enum EProductTabKey {
  OVERVIEW = 'overview',
  ANALYTICS = 'analytics',
  FEEDBACK = 'feedback',
}

export const PRODUCT_TABS: ITab<EProductTabKey>[] = [
  {
    key: EProductTabKey.OVERVIEW,
    label: 'Overview',
    startIcon: <Overview />,
  },
  {
    key: EProductTabKey.ANALYTICS,
    label: 'Analytics',
    startIcon: <AnalyticsIcon />,
  },
  {
    key: EProductTabKey.FEEDBACK,
    label: 'Feedback',
    startIcon: <FeedbackIcon />,
  },
];

const ProductOverview = () => {
  const theme = useTheme();
  const mediumScreenAndSmaller = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { id: paramProductId } = useParams<TRouteParamsWithId>();
  const { error, loading, data } = useQuery(GET_PRODUCT_AND_ANALYTICS_EXIST, {
    variables: { id: paramProductId },
  });

  const handleChangeTab = (tab: EProductTabKey) => {
    if (tab === EProductTabKey.OVERVIEW) {
      navigate(`/products/active/${paramProductId}`);
    } else {
      navigate(`/products/active/${paramProductId}/${tab}`);
    }
  };

  if (error) return <ErrorState />;
  if (!data && loading) return <Loader />;

  const { product, productAnalyticsExist } = data;
  const { publicUrl, defaultQrCode, title, id, slug } = product;

  return (
    <PageContainer>
      <PageTitle
        title="Product page overview"
        titleExtension={product?.title}
        goBackLabel="Product pages"
        goBackUrl="/products/active"
      />

      <Box mt={3} mb={2}>
        <Tabs
          tabs={PRODUCT_TABS}
          selectedTab={EProductTabKey.OVERVIEW}
          onChange={handleChangeTab}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ProductStatusCard product={product} />
        </Grid>
        <Grid item xs={12}>
          <Grid container direction={mediumScreenAndSmaller ? 'column-reverse' : 'row'} spacing={3}>
            <Grid item xs={mediumScreenAndSmaller ? 12 : 6}>
              <Grid container spacing={3}>
                <HasAccess
                  allowedPlans={[ECustomerPlan.PAYING, ECustomerPlan.INTERNAL, ECustomerPlan.TRIAL]}
                >
                  <Grid item xs={12}>
                    <ShareSummaryCard
                      productId={id}
                      publicUrl={publicUrl}
                      qrCode={defaultQrCode}
                      productTitle={title}
                      slug={slug}
                    />
                  </Grid>
                </HasAccess>
              </Grid>
            </Grid>
            <Grid item xs={mediumScreenAndSmaller ? 12 : 6}>
              <Grid container spacing={3}>
                {product && productAnalyticsExist && (
                  <Grid item xs={12}>
                    <ProductAnalyticsSmall product={product} />
                  </Grid>
                )}

                {/* Commented since the analytics article is not there yet, if the article is there please remove ProductAnalyticsSmall above and uncomment next section */}
                {/* <Grid item xs={12}>
                {
                  product && productAnalyticsExist ? (
                    <ProductAnalyticsSmall product={product} />
                  ) : null
                  <AnalyticsInspireCard />
                }
              </Grid> */}

                {/* Commented because the feedback article is not there yet */}
                {/* {!isDemo && (
                <Grid item xs={12}>
                  <FeedbackInspireCard />
                </Grid>
              )} */}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default ProductOverview;
